/* eslint-disable react/style-prop-object */
import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Spin } from 'antd';
import axios from 'axios';
import Utils from '../../../../misc/Utils';
import DiagnosticReportUtils from '../../../../misc/DiagnosticReportUtils';
import { IntroSum } from '../../diagnostic-components';

function getAssessmentId(assignmentId, fallbackAssessmentId) {
   return assignmentId !== undefined ? assignmentId : fallbackAssessmentId;
}

function getSummaryDataUrl({ assignmentId, assessmentId, sectionId, subjectId, schoolYear, version, preAssignmentId }) {
   const resolvedAssessmentId = getAssessmentId(assignmentId, assessmentId);
   const apiBase = process.env.REACT_APP_API_BRAINEE_DIAGNOSTIC;

   // if (version === 1) {
   //    return `${apiBase}/getdiagnosticreportdata?aid=${resolvedAssessmentId}&sid=${sectionId}`;
   // }

   // return `${apiBase}/v2/getdiagnosticreportdata?aid=${resolvedAssessmentId}&sids=${sectionId}&s=${subjectId}&sy=${schoolYear}`;
   return `${apiBase}/v3/getdiagnosticreportdata?aid=${assignmentId}&paid=${preAssignmentId}&sids=${sectionId}&s=${subjectId}&sy=${schoolYear}`;
}

function getSummaryPercentUrl({ assignmentId, assessmentId, sectionId, subjectId, schoolYear, version, preAssignmentId }) {
   const resolvedAssessmentId = getAssessmentId(assignmentId, assessmentId);
   const apiBase = process.env.REACT_APP_API_BRAINEE_DIAGNOSTIC;

   // if (version === 1) {
   //    return `${apiBase}/getdiagnosticsummarypercentdata?aid=${resolvedAssessmentId}&sid=${sectionId}`;
   // }

   // return `${apiBase}/v2/getdiagnosticsummarypercentdata?aid=${resolvedAssessmentId}&sids=${sectionId}&s=${subjectId}&sy=${schoolYear}`;
   return `${apiBase}/v3/getdiagnosticsummarypercentdata?aid=${assignmentId}&paid=${preAssignmentId}&sids=${sectionId}&s=${subjectId}&sy=${schoolYear}`;
}

function getAreaFields(area) {
   return Array.from({ length: area.count }, (_, i) => `s${area.start + i}`);
}

function getAverage(data, fields) {
   if (!data.length) return 0;
   return parseFloat((data.reduce((sum, item) => sum + fields.reduce((fieldSum, field) => fieldSum + item[field], 0), 0) / data.length).toFixed(2));
}

function computeSummaryStats(diagnosticType, preData, postData, areas, totalRanges) {
   const isPost = diagnosticType === 'post';
   const primaryData = isPost && postData.length ? postData : preData;

   const areaStats = {};
   areas.forEach((area) => {
      const fields = getAreaFields(area);
      const stats = Utils.calculateStats(primaryData, fields, area.mean, area.stdDev, area.ranges, isPost);

      if (postData.length && !isPost) {
         stats.postm = getAverage(postData, fields);
      } else {
         stats.prem = getAverage(preData, fields);
      }

      areaStats[area.key] = stats;
   });

   const total = Object.values(areaStats).reduce((acc, cat) => ({
      vn: isPost ? postData.length : preData.length,
      prem: acc.prem + cat.prem,
      postm: acc.postm + cat.postm,
      min: acc.min + cat.min,
      max: acc.max + cat.max,
      sd: acc.sd + cat.sd,
      beginner: acc.beginner + cat.beginner,
      basic: acc.basic + cat.basic,
      competent: acc.competent + cat.competent
   }), { prem: 0, postm: 0, min: 0, max: 0, sd: 0, beginner: 0, basic: 0, competent: 0 });

   const totalScore = postData.length ? total.postm : total.prem;
   total.proficiency = totalRanges.find(([min, max]) => totalScore >= min && totalScore <= max)?.[2] || '';

   return { areaStats, total };
}

export function SharedEMSSummaryReport({ config, ...props }) {
   const assignmentId = props.assignment_id;
   const assessmentId = props.assessment_id;
   const sectionId = props.section_id;
   const subjectId = props.subject_id;
   const schoolYear = props.school_year;
   const version = props.version;
   const diagnosticType = props.diagnostic_type;
   const preAssignmentId = props.pre_assignment_id;

   const [reportData, setReportData] = useState(null);
   const [pcData, setPcData] = useState({});
   const [summaryStats, setSummaryStats] = useState({});
   const [dataLoading, setDataLoading] = useState(false);

   const activeSubjects = useMemo(() => {
      if (!subjectId || subjectId === 'ems') return ['english', 'math', 'science'];
      if (subjectId === 'english' || subjectId === 'math' || subjectId === 'science') return [subjectId];
      return [];
   }, [subjectId]);

   useEffect(() => {
      Utils.verifyJwt();
      setDataLoading(true);

      const requestProps = { assignmentId, assessmentId, sectionId, subjectId, schoolYear, version, preAssignmentId };

      axios.get(getSummaryDataUrl(requestProps)).then((response) => {
         if (!response.data) {
            setDataLoading(false);
            return;
         }

         const data = response.data;

         axios.get(getSummaryPercentUrl(requestProps)).then((percentResponse) => {
            const percentData = percentResponse.data || [];
            const nextPc = {};
            const nextStats = {};

            activeSubjects.forEach((subject, idx) => {
               const subjectConfig = config[subject];
               if (!subjectConfig) return;

               const preData = data.filter((item) => item.subject === subject && item.diagnostic_type === 'pre');
               const postData = data.filter((item) => item.subject === subject && item.diagnostic_type === 'post');

               nextStats[subject] = computeSummaryStats(diagnosticType, preData, postData, subjectConfig.summaryAreas, subjectConfig.totalRanges);
               nextPc[subject] = (subjectId && subjectId !== 'ems') ? (percentData[0] || null) : (percentData[idx] || null);
            });

            setPcData(nextPc);
            setSummaryStats(nextStats);
            setReportData(data);
            setDataLoading(false);
         }, () => setDataLoading(false));
      }, () => setDataLoading(false));
   }, [assignmentId, assessmentId, sectionId, subjectId, schoolYear, version, diagnosticType, activeSubjects, config]);

   const diagnosticDate = useMemo(() => {
      if (!reportData || reportData.length === 0) return null;

      const matchedData = reportData.find((item) => item.diagnostic_type === diagnosticType);
      return (matchedData || reportData[0]).diagnostic_date;
   }, [reportData, diagnosticType]);

   if (dataLoading) {
      return (
         <div className='center-empty-space'>
            <Spin size='large' tip="Loading data, please wait..." />
         </div>
      );
   }

   if (reportData === null || reportData.length === 0 || activeSubjects.length === 0) {
      return (
         <div className='center-empty-space'>
            <Empty description='No Data Found' image="../images/brainee_mascot.png" />
         </div>
      );
   }

   return (
      <div className="dashboard-item-inner3" style={{ margin: '20px' }}>
         <div style={{ fontFamily: 'calibri' }} id='summary-report'>
            <div>
               <IntroSum
                  grade_level={props.template}
                  section={props.section_desc}
                  school_name={reportData[0].school_name}
                  subject_id={props.subject_id}
                  subject={props.subject}
                  backgroundColor="#000000"
                  diagnostic_date={diagnosticDate}
                  diagnostic_type={props.diagnostic_type}
               />

               <p style={{ marginTop: '0pt', marginBottom: '0pt', textAlign: 'center' }}><strong>&nbsp;</strong></p>
               <p style={{ marginTop: '0pt', marginBottom: '0pt', textAlign: 'center' }}><strong>SUMMARY OF RESULTS FOR {props.template}</strong></p>
               <p style={{ marginTop: '0pt', marginBottom: '0pt' }}><strong>&nbsp;</strong></p>
               <p style={{ marginTop: '0pt', marginBottom: '0pt' }}><strong>MEAN LEVEL OF SCORES IN THE SUBJECT AREAS</strong></p>
               <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                     {DiagnosticReportUtils.generateMeandLevelHeader()}
                     {activeSubjects.map((subject) => {
                        const subjectConfig = config[subject];
                        const subjectStats = summaryStats[subject];
                        if (!subjectConfig || !subjectStats) return null;

                        return (
                           <React.Fragment key={`mean-${subject}`}>
                              {subjectConfig.summaryAreas.map((area) => (
                                 <React.Fragment key={`mean-${subject}-${area.key}`}>
                                    {DiagnosticReportUtils.generateMeanLevelRow(area.label, subjectStats.areaStats[area.key], false)}
                                 </React.Fragment>
                              ))}
                              {DiagnosticReportUtils.generateMeanLevelRow(subject.toUpperCase(), subjectStats.total, false)}
                           </React.Fragment>
                        );
                     })}
                  </tbody>
               </table>

               {activeSubjects.map((subject) => {
                  const subjectConfig = config[subject];
                  const subjectStats = summaryStats[subject];
                  if (!subjectConfig || !subjectStats) return null;

                  const contentStandards = [
                     ...subjectConfig.summaryAreas.map((area) => ({
                        label: area.label,
                        beg1: area.ranges[0][0],
                        beg2: area.ranges[0][1],
                        bas1: area.ranges[1][0],
                        bas2: area.ranges[1][1],
                        comp1: area.ranges[2][0],
                        comp2: area.ranges[2][1],
                        data: subjectStats.areaStats[area.key],
                     })),
                     {
                        label: subject.toUpperCase(),
                        beg1: subjectConfig.totalRanges[0][0],
                        beg2: subjectConfig.totalRanges[0][1],
                        bas1: subjectConfig.totalRanges[1][0],
                        bas2: subjectConfig.totalRanges[1][1],
                        comp1: subjectConfig.totalRanges[2][0],
                        comp2: subjectConfig.totalRanges[2][1],
                        data: subjectStats.total,
                     }
                  ];

                  const competenciesByIndex = Object.fromEntries(subjectConfig.competencies.map((item) => [item.index, item.label]));
                  const subjectPcData = pcData[subject];

                  return (
                     <React.Fragment key={`subject-${subject}`}>
                        <p style={{ marginTop: '0pt', marginBottom: '0pt' }}><strong>&nbsp;</strong></p>
                        <p style={{ marginTop: '0pt', marginBottom: '0pt' }}><strong>INTERPRETATION ({subject.toUpperCase()})</strong></p>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                           <tbody>
                              <tr style={{ height: '14.5pt' }}>
                                 <td style={{ width: '39.3%', borderTop: '1pt solid #000000', borderLeft: '1pt solid #000000', paddingRight: '5.4pt', paddingLeft: '4.9pt', verticalAlign: 'middle', backgroundColor: '#000000' }}>
                                    <p style={{ textAlign: 'center', marginBottom: '0pt' }}><strong><span style={{ color: '#ffffff' }}>Content Standards</span></strong></p>
                                 </td>
                                 <td colSpan={3} style={{ borderTop: '1pt solid #000000', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#000000' }}>
                                    <p style={{ textAlign: 'center', marginBottom: '0pt' }}><strong><span style={{ color: '#ffffff' }}>Beginner</span></strong></p>
                                 </td>
                                 <td colSpan={3} style={{ borderTop: '1pt solid #000000', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#000000' }}>
                                    <p style={{ textAlign: 'center', marginBottom: '0pt' }}><strong><span style={{ color: '#ffffff' }}>Basic</span></strong></p>
                                 </td>
                                 <td colSpan={3} style={{ borderTop: '1pt solid #000000', borderRight: '1pt solid #000000', paddingRight: '4.9pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#000000' }}>
                                    <p style={{ textAlign: 'center', marginBottom: '0pt' }}><strong><span style={{ color: '#ffffff' }}>Competent</span></strong></p>
                                 </td>
                              </tr>
                              {contentStandards.map((row) => (
                                 <React.Fragment key={`std-${subject}-${row.label}`}>
                                    {DiagnosticReportUtils.generateContentStandards(row.label, row.beg1, row.beg2, row.bas1, row.bas2, row.comp1, row.comp2)}
                                 </React.Fragment>
                              ))}
                           </tbody>
                        </table>

                        {contentStandards.map((row, i) => (
                           <React.Fragment key={`freq-${subject}-${row.label}`}>
                              {DiagnosticReportUtils.generateFrequencyRow(row.label, row.data, i + 1)}
                           </React.Fragment>
                        ))}

                        <p style={{ marginTop: '0pt', marginBottom: '10pt', lineHeight: '115%', fontSize: '12pt' }}><strong>&nbsp;</strong></p>
                        <p style={{ marginTop: '0pt', marginBottom: '10pt', lineHeight: '115%', fontSize: '12pt' }}><strong>Percent Correct for {subject.toUpperCase()}</strong></p>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                           <tbody>
                              <tr>
                                 <td colSpan={2} style={{ borderTop: '1pt solid #000000', borderLeft: '1pt solid #000000', paddingRight: '5.4pt', paddingLeft: '4.9pt', verticalAlign: 'middle', backgroundColor: '#000000' }}>
                                    <p style={{ textAlign: 'center' }}><strong><span style={{ color: '#ffffff' }}>LEARNING COMPETENCY</span></strong></p>
                                 </td>
                                 <td style={{ width: '19.9%', borderTop: '1pt solid #000000', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#000000' }}>
                                    <p style={{ textAlign: 'center' }}><strong><span style={{ color: '#ffffff' }}>Pre Test Percent Correct for the Level</span></strong></p>
                                 </td>
                                 <td style={{ width: '20.6%', borderTop: '1pt solid #000000', borderRight: '1pt solid #000000', paddingRight: '4.9pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#000000' }}>
                                    <p style={{ textAlign: 'center' }}><strong><span style={{ color: '#ffffff' }}>Post Test Percent Correct for the Level</span></strong></p>
                                 </td>
                              </tr>
                              {subjectConfig.sections.map((section) => (
                                 <React.Fragment key={`sec-${subject}-${section.label}`}>
                                    {DiagnosticReportUtils.renderLabel(section.label)}
                                    {Array.from({ length: section.end - section.start + 1 }, (_, offset) => {
                                       const index = section.start + offset;
                                       return (
                                          <React.Fragment key={`pc-${subject}-${index}`}>
                                             {DiagnosticReportUtils.generatePercentCorrectRow(
                                                competenciesByIndex[index] || '',
                                                subjectPcData ? subjectPcData[`pre_s${index}`] : null,
                                                subjectPcData ? subjectPcData[`post_s${index}`] : null,
                                                index
                                             )}
                                          </React.Fragment>
                                       );
                                    })}
                                 </React.Fragment>
                              ))}
                           </tbody>
                        </table>
                     </React.Fragment>
                  );
               })}
            </div>
         </div>
      </div>
   );
}
